import { AUDIBLE_NOTIFICATION_TYPES, type NotificationType } from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Phone } from 'lucide-react';
import { useEffect } from 'react';
import { api } from '@/api/client';
import { notificationsApi } from '@/api/module-client';
import type { ToastApi } from '@/components/ui/toast';
import { notificationKeys } from '@/features/notifications/use-notifications';
import type { AlertSound } from './use-alert-sound';
import type { DesktopNotifications } from './use-desktop-notifications';
import type { NotificationEvent } from './use-live-events';

/**
 * A notification arrived for me — make it felt, not just stored.
 *
 * Three things happen, and the ORDER is the design. The bell is refreshed
 * first so the count is right the instant anybody looks; then the chime;
 * then the OS banner, which draws only when the tab is not in front.
 *
 * WHY THE SOCKET RATHER THAN THE POLL. The bell polls every sixty seconds
 * and `refetchIntervalInBackground` is off, so a backgrounded tab does not
 * poll at all. Reminders now fire ten minutes before the call and the chase
 * five minutes before, so a minute of polling latency is a tenth and then a
 * fifth of the warning — and in a background tab it is unbounded.
 *
 * NOT EVERY NOTIFICATION MAKES A NOISE. `AUDIBLE_NOTIFICATION_TYPES` is the
 * list, and `lead_assigned` and `target_set` are deliberately not on it. A
 * chime for everything is a chime people mute, and then the reminders that
 * mattered are silent too.
 */
/**
 * Which notifications have already been acted on, at MODULE scope.
 *
 * TWO bugs were needed to get this right, and both were found by playing a
 * real reminder through a real browser rather than by reading the code.
 *
 * First a `useRef`, which is recreated on remount — and StrictMode mounts
 * everything twice in development, so the guard reset and each reminder
 * chimed twice. Module scope fixed that.
 *
 * Then it chimed twice again, because StrictMode also opens TWO SOCKETS:
 * both delivered the same notification, and the guard was keyed on arrival
 * time, which differs by a millisecond between them. Arrival time cannot
 * distinguish two deliveries of one notification from two notifications of
 * the same type — only the row's own id can, which is why the server now
 * sends it.
 *
 * A Set rather than a single value, because two notifications can arrive in
 * either order across two sockets. Bounded below, since this lives as long
 * as the tab does.
 */
const handled = new Set<number>();
/** A day of reminders is dozens, not thousands; this is paranoia, cheaply. */
const HANDLED_MAX = 500;

export function useReminderAlerts(
  event: NotificationEvent | null,
  myUserId: string | undefined,
  sound: AlertSound,
  desktop: DesktopNotifications,
  toast: ToastApi,
): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!event || !myUserId) return;
    if (handled.has(event.notificationId)) return;

    /*
     * MINE ONLY. The socket is per ORG — the call banner is something the
     * whole team may see — but a reminder belongs to the person it was
     * written for. Without this, everyone's tab chimes for everyone's
     * follow-ups, which is the fastest way to get the feature turned off.
     */
    if (event.recipientId !== myUserId) return;

    handled.add(event.notificationId);
    if (handled.size > HANDLED_MAX) {
      // Oldest first — insertion order is what a Set iterates in.
      for (const id of handled) {
        handled.delete(id);
        if (handled.size <= HANDLED_MAX / 2) break;
      }
    }

    // First, so the count is right even if the operator looks before the
    // sound finishes.
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });

    if (!AUDIBLE_NOTIFICATION_TYPES.includes(event.notificationType as NotificationType)) return;

    sound.play();

    /*
     * SOMETHING ON SCREEN, ALWAYS. The first version played the sound and
     * drew nothing: `desktop.show` returns early while the tab is visible —
     * correctly, an OS banner over the app you are looking at is noise —
     * and there was no in-app equivalent behind it. So an operator sitting
     * in front of the CRM heard a chime and saw nothing, which is worse
     * than silence because it reads as a glitch rather than a reminder.
     *
     * The text is FETCHED rather than carried on the event. The event has
     * only a type, and the bell is the one place that decides what a
     * notification says; inventing a second wording here is how the toast
     * and the panel end up disagreeing about the same row.
     */
    void notificationsApi
      .list({ unreadOnly: true, limit: 1 })
      .then((page) => {
        const newest = page.items[0];
        if (!newest) return;

        const phone = newest.contactPhone;
        const followUpId = newest.followUpId;

        const refresh = () => void queryClient.invalidateQueries();

        /**
         * Push it back, from the reminder itself.
         *
         * A reminder arrives mid-call with somebody else at least as often as
         * it arrives at a free moment, and the only honest answers were "drop
         * everything" or "let it go overdue". Reschedule from NOW rather than
         * from the original due time, because ten minutes after a reminder
         * that is already late would land in the past and fire again at once.
         */
        const snooze = (minutes: number) => ({
          label: `+${minutes}m`,
          'aria-label': `Snooze ${minutes} minutes`,
          onClick: () => {
            void api.followUps
              .reschedule(followUpId as string, {
                dueAt: new Date(Date.now() + minutes * 60_000).toISOString(),
                remindBeforeMinutes: 0,
              })
              .then(() => {
                toast.success(`Snoozed ${minutes} minutes`);
                refresh();
              })
              .catch(() => toast.error('Could not snooze it'));
          },
        });

        toast.toast({
          tone: event.notificationType === 'follow_up_chase' ? 'error' : 'info',
          title: newest.title,
          description: newest.body ?? undefined,
          /*
           * A reminder stays until it is dealt with. The default four and a
           * half seconds is right for "saved"; for "ring this person in ten
           * minutes" it means the number is gone before the operator has
           * finished the sentence they were in.
           */
          persist: true,
          actions: [
            ...(phone
              ? [
                  {
                    label: 'Call',
                    icon: Phone,
                    'aria-label': `Call ${phone}`,
                    // `tel:` rather than the in-app dialler: on a desk this
                    // hands off to whatever the operator actually dials with,
                    // and on a phone it is the native call screen.
                    onClick: () => {
                      window.location.href = `tel:${phone}`;
                    },
                  },
                  {
                    label: 'Copy',
                    icon: Copy,
                    'aria-label': `Copy ${phone}`,
                    // Stays open: the next thing wanted is usually the Call
                    // button sitting beside it.
                    keepOpen: true,
                    onClick: () => {
                      void navigator.clipboard
                        .writeText(phone)
                        .then(() => toast.success('Number copied'))
                        /* Clipboard access is refused outside a secure context
                           and in some embedded webviews. Saying so beats a
                           button that looks like it worked. */
                        .catch(() => toast.error('Could not copy the number'));
                    },
                  },
                ]
              : []),
            ...(followUpId ? [snooze(10), snooze(30)] : []),
            /*
             * DONE, on the reminder itself.
             *
             * This is the moment the call actually happens — the chime goes,
             * they ring the customer, and then they are looking at this
             * toast. Until this existed the only way to close a follow-up was
             * the Follow-ups page, and the result was thirty-five created and
             * none ever completed. The cheapest place to put the button is the
             * thing already on screen.
             *
             * Last in the row deliberately: it is the only one that cannot be
             * undone, so it should not sit where a thumb lands by accident.
             */
            ...(followUpId
              ? [
                  {
                    label: 'Done',
                    icon: Check,
                    'aria-label': 'Mark this follow-up done',
                    onClick: () => {
                      void api.followUps
                        .close(followUpId, { status: 'completed' })
                        .then(() => {
                          toast.success('Marked done');
                          refresh();
                        })
                        .catch(() => toast.error('Could not mark it done'));
                    },
                  },
                ]
              : []),
          ],
        });
        // Only when they are looking elsewhere. Both would be two of the
        // same thing, one of them on top of the other.
        desktop.show(newest.title, newest.body ?? undefined, `crm-${event.notificationType}`);
      })
      .catch(() => {
        // The bell still has it, and the chime already played. A failed
        // read must not become an error on screen about a reminder.
      });
  }, [event, myUserId, sound, desktop, toast, queryClient]);
}
